import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { RiderCompensationReasons } from '../../../consts';
import { OPS_TIMEZONE } from '../../../helpers/utils/opsTime';
import { getProductFromRide, getRouteFromRide } from '../../../helpers/utils/ride';
import { useFormatters, useTextMasks, useToast } from '../../../hooks';
import { getRideHistory, refundRide } from '../../../redux/Users/actions';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Pagination,
  SelectWithDescription,
  Stack,
  TextField,
  TintedStatusPill,
  Typography,
} from '../../../ui/core';
import { hairline, softFill } from '../../../ui/styles';
import { selectWithDescriptionDefaultValidationSchema } from '../../../ui/core/SelectWithDescription';
import { CloseIcon, RideIcon } from '../../../ui/icons';
import { RideStatusChip } from '../Trips/RidersList';
import { StandbyStatusChip } from '../Rides/StandbyStatusChip';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  PageLink,
  RowMenu,
  TrailingMeta,
  Meta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

const REASON_FOR_REFUNDING_OTHER_ID = 'OTHER';

function ReasonForRefunding({ control }) {
  return (
    <SelectWithDescription
      alwaysShowDescription
      control={control}
      focusDescription
      id="reason-for-refunding-filter-label-id"
      labelDescription="Reason"
      labelId="reason-for-refunding-filter-label"
      name="selectedReasonForRefunding"
      options={RiderCompensationReasons}
      otherOptionId={REASON_FOR_REFUNDING_OTHER_ID}
      otherOptionLabel="Description"
      placeholder="Select Reason"
      showOtherOption
    />
  );
}

// `refundPolicy` is the policy the ride was booked under, as the backend describes it.
// It is absent when the admin backend predates the field, and then nothing renders
// rather than a guess: the rules live server-side and a client copy would drift.
//
// Only a booking made non-refundable (the shared non-refundable test) is called out.
// Every other policy is the ordinary case, and a pill on each of those rows is noise;
// "Refund policy" on the meta line covers them.
const NonRefundablePill = ({ policy }) =>
  policy?.kind === 'NON_REFUNDABLE' ? <TintedStatusPill dense palette="warning" label="Non-refundable" /> : null;

const RefundPolicyLink = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      p: 0,
      border: 0,
      background: 'none',
      font: 'inherit',
      color: 'text.primary',
      cursor: 'pointer',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    }}
  >
    Refund policy
  </Box>
);

const RefundPolicyDialog = ({ policy, onClose }) => (
  <Dialog open onClose={onClose}>
    <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
      <DialogTitle sx={{ padding: 0 }}>Refund policy</DialogTitle>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Box>
    <Box padding="24px 32px 32px 32px" width="400px">
      <CancellationPolicySection policy={policy} />
    </Box>
  </Dialog>
);

// Also shown before the refund form, so whoever is about to refund sees the terms the
// rider booked under, above all a ride from the shared non-refundable test.
export const CancellationPolicySection = ({ policy }) => (
  <Box sx={{ padding: '14px 16px', borderRadius: '10px', backgroundColor: (t) => softFill(t) }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      Cancellation policy
    </Typography>
    <Typography
      sx={{
        marginTop: '4px',
        fontSize: '14px',
        fontWeight: 600,
        color: policy.refundable ? 'text.primary' : 'warning.main',
      }}
    >
      {policy.title}
    </Typography>
    <Stack component="ul" spacing="4px" sx={{ margin: '8px 0 0', paddingLeft: '18px', listStyleType: 'disc' }}>
      {policy.details.map((line) => (
        <Typography
          component="li"
          key={line}
          sx={{ display: 'list-item', fontSize: '12.5px', color: 'text.secondary' }}
        >
          {line}
        </Typography>
      ))}
    </Stack>
  </Box>
);

const RefundDialog = ({ user, ride, onClose }) => {
  const t = useToast();
  const dispatch = useDispatch();
  const m = useTextMasks();
  const maxRefundAmount = ride.fare ? ride.fare.finalAmount : 0;

  const selectReasonForRefundingValidationSchema = selectWithDescriptionDefaultValidationSchema({
    entity: 'Reason',
    otherOptionId: REASON_FOR_REFUNDING_OTHER_ID,
    labelToEnforceDescription: 'Please specify reason',
  });

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: `${maxRefundAmount}`,
      selectedReasonForRefunding: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        amount: yup
          .number()
          .required('Amount is required')
          .typeError('Amount is required')
          .min(0, 'Refund amount must be positive')
          .max(maxRefundAmount, `Refund amount must be lower than $${maxRefundAmount}`),
        selectedReasonForRefunding: selectReasonForRefundingValidationSchema,
      }),
    ),
  });

  const { isSubmitting } = useFormState({ control: form.control });

  const onSubmit = async (userFormData) => {
    const { success, error } = await refundRide({
      userId: user.id,
      rideId: ride.rideId || ride.id,
      amount: userFormData.amount,
      reasonId: userFormData.selectedReasonForRefunding.id,
      reasonDescription: userFormData.selectedReasonForRefunding.description,
    })(dispatch);
    success ? t.success(`Successfully refunded $${userFormData.amount}`) : t.error(`Error: ${error.message}`);
    success && onClose();
  };

  return (
    <Dialog open onClose={onClose}>
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Refund Ride</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box component="form" onSubmit={form.handleSubmit((data) => onSubmit(data))}>
        <Box padding="32px 32px 32px 32px" width="400px">
          {ride.refundPolicy ? (
            <Box marginBottom="24px">
              <CancellationPolicySection policy={ride.refundPolicy} />
            </Box>
          ) : null}
          <Box>
            <TextField
              name="amount"
              label="Amount"
              type="number"
              mask={m.text.number2F}
              control={form.control}
              InputProps={{
                startAdornment: '$',
              }}
              fullWidth
            />
          </Box>

          <Box marginTop="24px">
            <ReasonForRefunding control={form.control} />
          </Box>
        </Box>

        <Button
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
          variant="contained"
          size="large"
          disableElevation
          sx={{ width: '100%', padding: '20px', borderRadius: '0 0 4px 4px' }}
        >
          {isSubmitting ? 'Refunding...' : 'Refund'}
        </Button>
      </Box>
    </Dialog>
  );
};

export const RideItem = ({ user, ride, first }) => {
  const f = useFormatters();
  const rideId = ride.id || ride.rideId;
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);

  const hasRefundPermission = useHasPermission('refund');
  const canRefund = hasRefundPermission && !ride.didRefundCash;

  const route = getRouteFromRide(ride);
  const product = getProductFromRide(ride);

  // The clock the RIDE runs on, not the one the viewer is sitting in.
  //
  // A standby has no committed departure instant, so the backend encodes the calendar
  // date as midnight in the route's zone — 2026-09-25T04:00:00.000Z for an Eastern
  // origin. Sitting exactly on the day boundary leaves no tolerance: formatted
  // browser-local it names the previous day for any viewer west of the route, and
  // support sits in Central, so every Florida standby read a day early (HITCH-5370).
  //
  // `toTimezoneLocaleString` falls back to UTC when the zone is missing, which is what
  // rows cached from before the backend carried one will hit.
  const rideZone = ride.originStation?.timeZone;
  const onRideClock = (value, format) => f.date.toTimezoneLocaleString(value, rideZone, format);

  // Standby departures are a DATE, not a datetime — the ride is not attached to a
  // departure time until it is confirmed. Printing a clock time for one would invent
  // "12:00 AM", so the meta line carries the product alone in that case.
  const departureTime =
    product === 'Standby' ? null : onRideClock(ride.departureTime, f.date.DateFormats.TIME_SIMPLE);

  // The trip, not the ride. The trip page carries the driver, the other riders and the
  // itinerary — which is what a support agent is actually looking at when they open a
  // row from someone's history. Falls back to the ride page when the ride has no trip
  // yet: a pending standby ride is not attached to one until it is confirmed, and a
  // link to the ride beats a dead row.
  // NOT the ride's clock, and not the browser's: `/trips/daily` reads `date` as an
  // ops day and bounds its query with `opsDayRange`, which is Central. A ride
  // departing just after midnight Eastern belongs to the PREVIOUS Central ops day, so
  // dating this link on the route's clock would link to a day whose window does not
  // contain the trip — the row would open a page the trip is missing from. The gutter
  // date above and this parameter answer two different questions and legitimately
  // disagree for such a ride.
  const tripUrl = ride.tripId
    ? `/trips/daily?date=${f.date.toTimezoneLocaleString(ride.departureTime, OPS_TIMEZONE, f.date.DateFormats.DATE_ISO)}` +
      `&tripId=${ride.tripId}&fullScreen=true`
    : null;
  const rowUrl = tripUrl || (rideId ? `/rides/${rideId}` : null);

  return (
    <>
      <Row
        first={first}
        // Date only in the gutter; the time goes on the meta line, so the two are not
        // printed twice on one row.
        at={onRideClock(ride.departureTime, f.date.DateFormats.DATE_SHORT)}
        // The route is the row: it is what a support agent is scanning for, and the
        // trip is what they open next. The old row led with the timestamp and
        // put "View ride" on a third line as a button.
        // The non-refundable pill sits on the route rather than with the status chip on
        // the trailing edge, which keeps those chips lined up down the list.
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            {rowUrl ? <PageLink to={rowUrl}>{route}</PageLink> : route}
            <NonRefundablePill policy={ride.refundPolicy} />
          </Box>
        }
        // The fare lives here now, where the "Trip" link used to be. It was on the
        // trailing edge behind a variable-width status chip ("Cancelled" vs "Completed"),
        // so the prices sat at a different x on every row and the chips never aligned.
        // There is no separate trip link any more — the row label IS it.
        meta={
          <>
            {[departureTime, product, ride.fare && f.number.currency(ride.fare.finalAmount)]
              .filter(Boolean)
              .join(' · ')}
            {ride.refundPolicy ? (
              <>
                {' · '}
                <RefundPolicyLink onClick={() => setShowPolicyDialog(true)} />
              </>
            ) : null}
          </>
        }
        right={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* A standby row carries both statuses. `status` is translated and
                collapses CANCELLED with UNCONFIRMED, so prefer the raw one
                when it is there (HITCH-3277). */}
            {ride.standbyStatus ? (
              <StandbyStatusChip status={ride.standbyStatus} />
            ) : (
              <RideStatusChip status={ride.status} />
            )}
            {/* Refund is money leaving Hitch and it cannot be taken back, so it goes
                behind the overflow rather than sitting as a one-click icon on every row
                of a paged history. Same treatment as Reverse on the payment log. */}
            <RowMenu
              disabled={!canRefund}
              label={`Actions for the ${route} ride`}
              items={[{ label: 'Refund ride', danger: true, onClick: () => setShowRefundDialog(true) }]}
            />
          </Stack>
        }
      />

      {showRefundDialog ? <RefundDialog user={user} ride={ride} onClose={() => setShowRefundDialog(false)} /> : null}
      {showPolicyDialog ? (
        <RefundPolicyDialog policy={ride.refundPolicy} onClose={() => setShowPolicyDialog(false)} />
      ) : null}
    </>
  );
};

const RideHistoryCard = ({ user }) => {
  const dispatch = useDispatch();
  const userRideHistory = useSelector((state) => state.users.selection.rideHistory);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const pages = userRideHistory ? Math.ceil(userRideHistory.count / pageSize) : 0;

  const onPageChange = (_, value) => {
    setPage(value);
  };

  const load = useCallback(
    () => getRideHistory(user.id, (page - 1) * pageSize, pageSize)(dispatch),
    [page, pageSize, dispatch, user.id],
  );
  const [status, retry] = useSectionFetch(load);

  return (
    <>
      <SectionCard
        title="Ride history"
        icon={<RideIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          Number.isFinite(userRideHistory?.count) ? (
            <TrailingMeta>
              <Meta>{userRideHistory.count} total</Meta>
            </TrailingMeta>
          ) : null
        }
      >
        {status === 'loading' && <SectionSkeleton />}
        {status === 'failed' && (
          <SectionError onRetry={retry}>Couldn&rsquo;t load this rider&rsquo;s rides.</SectionError>
        )}
        {status === 'done' && !userRideHistory?.data?.length && <EmptyRows>No rides yet.</EmptyRows>}
        {status === 'done' &&
          userRideHistory?.data?.map((ride, idx) => {
            const rideId = ride.id || ride.rideId;
            return <RideItem key={`${rideId}-${idx}`} first={idx === 0} user={user} ride={ride} />;
          })}
        {pages > 1 && (
          <Box sx={{ px: 1.75, py: 1.25, borderTop: (t) => `1px solid ${hairline(t)}` }}>
            {/* `page` was never passed, so the control tracked its own state and drifted
                from the page actually fetched as soon as anything else reset it. */}
            <Pagination count={pages} page={page} size="small" onChange={onPageChange} />
          </Box>
        )}
      </SectionCard>
    </>
  );
};

export default RideHistoryCard;
